import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Tile } from "components/UIComponents/Tile";
import { TileWrapper } from "components/UIComponents/TileWrapper.jsx";
import { FormDate, FormInput } from "components/Matters/index";
import SubmitButton from "components/common/SubmitButton";
import AuthCodeDialog from "components/common/AuthCodeDialog";
import {
  AmountFormat,
  formatDateForAPI,
  roundTo2,
} from "components/common/formatter";
import { CONSTANT } from "CONSTANT/Matter/MatterForm";
import { LINQ_STATUS } from "CONSTANT/statusCodes";
import {
  useAccessRight,
  CaseAccessRights,
} from "components/UserAccessRights.ts";
import { writeOffWIP } from "api/index";
import { useMatterBalances } from "services/matterBalancesService";
import {
  closeMatter,
  fetchMatterAllInfo,
  getCanClose,
  reopenMatter,
} from "services/mattersService";
import {
  COMPLETION_BALANCE_ROWS,
  getNonZeroBalances,
} from "./matterCompletionUtils";

const toDate = (value) => (value ? new Date(value) : null);

const readAuthError = (error) =>
  error?.status === LINQ_STATUS.InvalidUserAuthCode
    ? error?.data?.message || "Invalid authorisation code."
    : "";

const ConfirmDialog = ({ content, isLoading, onYes, onNo }) => (
  <Dialog title="Please confirm" width={500} onClose={onNo}>
    <p className="m-3 text-center">{content}</p>
    <DialogActionsBar>
      <div className="dialog-footer">
        <Button
          type="button"
          className="common-btn bgBtnSecondary border-0 me-2"
          onClick={onNo}
          disabled={isLoading}
        >
          No
        </Button>
        <SubmitButton
          type="button"
          className="me-4"
          onClick={onYes}
          isLoading={isLoading}
        >
          Yes
        </SubmitButton>
      </div>
    </DialogActionsBar>
  </Dialog>
);

const MatterCompletionTab = ({
  matterId,
  matter,
  onMatterClosed,
  onMatterReopened,
  onWipWrittenOff,
  children,
}) => {
  const dispatch = useDispatch();
  const canWriteOffWip = useAccessRight(CaseAccessRights.WriteOffWIP);

  const isClosed = Boolean(matter?.closed);
  const [dateState, setDateState] = useState({
    [CONSTANT.DATE.CLOSED_DATE]: new Date(),
  });
  const locationValue = matter?.location || "";

  const {
    data: balancesData,
    loading: balancesLoading,
    refetch: refetchBalances,
  } = useMatterBalances(matterId);
  const balances = balancesData?.matterBalances ?? null;
  const [confirm, setConfirm] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [closeCheck, setCloseCheck] = useState(null);
  const [authAction, setAuthAction] = useState(null);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    setDateState({
      [CONSTANT.DATE.CLOSED_DATE]:
        toDate(matter?.dateClosed) || (isClosed ? null : new Date()),
    });
  }, [matter?.matterId, matter?.closed, matter?.dateClosed]);

  const loadCanClose = async () => {
    if (!matterId) return null;
    try {
      const check = await getCanClose(matterId);
      setCloseCheck(check);
      return check;
    } catch (error) {
      console.error("Failed to check if matter can be closed", error);
      setCloseCheck(null);
      return null;
    }
  };

  useEffect(() => {
    setCloseCheck(null);
    if (!isClosed) loadCanClose();
  }, [matterId, isClosed]);

  const nonZeroBalances = balances ? getNonZeroBalances(balances) : [];
  const wipBalance = roundTo2(balances?.wipBalance ?? 0);
  const closedDate = dateState[CONSTANT.DATE.CLOSED_DATE];
  const canClose =
    !isClosed &&
    !balancesLoading &&
    balances !== null &&
    closeCheck?.allowed !== false;

  const handleCloseClick = async () => {
    if (!closedDate) {
      toast.error("Please select a closed date.");
      return;
    }
    setIsSubmitting(true);
    try {
      const check = await loadCanClose();
      if (check && !check.allowed) return;
      setConfirm("close");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseMatter = async () => {
    setIsSubmitting(true);
    try {
      const res = await closeMatter(matterId, {
        closed: true,
        dateClosed: formatDateForAPI(closedDate),
      });
      if (res?.status === 200) {
        toast.success("Matter Closed Successfully");
        dispatch(fetchMatterAllInfo(matterId));
        onMatterClosed?.(closedDate);
      }
    } catch (error) {
      console.error("Failed to close matter", error);
    } finally {
      setIsSubmitting(false);
      setConfirm(null);
    }
  };

  const openAuthDialog = (action) => {
    setAuthError("");
    setAuthAction(action);
  };

  const closeAuthDialog = () => {
    setAuthError("");
    setAuthAction(null);
  };

  const handleReopenMatter = async (authCode) => {
    setIsSubmitting(true);
    setAuthError("");
    try {
      const res = await reopenMatter(matterId, authCode);
      if (res?.status === 200) {
        toast.success("Matter Reopened Successfully");
        dispatch(fetchMatterAllInfo(matterId));
        closeAuthDialog();
        onMatterReopened?.();
      }
    } catch (error) {
      setAuthError(readAuthError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWriteOffWip = async (authCode) => {
    setIsSubmitting(true);
    setAuthError("");
    try {
      const res = await writeOffWIP(matterId, {
        authCode,
        wipValue: wipBalance,
      });
      if (res?.status === 200 || res?.status === 201) {
        toast.success("WIP Written Off Successfully");
        dispatch(fetchMatterAllInfo(matterId));
        closeAuthDialog();
        await refetchBalances();
        await loadCanClose();
        onWipWrittenOff?.();
      }
    } catch (error) {
      setAuthError(readAuthError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TileWrapper>
      <div className="row g-2">
        <div className="col-12 col-xl-6 d-flex flex-column gap-2">
          <Tile
            heading="Completion"
            className={`px-4 pt-2 pb-3 overflow-auto ${
              children ? "" : "flex-grow-1"
            }`}
          >
            <Checkbox label="Closed" value={isClosed} readOnly={true} />
            <FormDate
              disabled={isClosed}
              wf={true}
              isStyled={true}
              className={"mt-3 form-date-picker note-date-picker"}
              width={"260px"}
              label={CONSTANT.DATE.CLOSED_DATE}
              onChangeDate={(e, label) =>
                setDateState((prev) => ({ ...prev, [label]: e.value }))
              }
              state={dateState}
            />
            <div className="d-flex align-items-end justify-content-between mt-3">
              <FormInput
                state={{ [CONSTANT.INPUT.LOCATION]: locationValue }}
                wf={true}
                isStyled={true}
                width={"260px"}
                label={CONSTANT.INPUT.LOCATION}
                ph="Enter Location"
                disabled={true}
              />
              {isClosed ? (
                <Button
                  type="button"
                  className="common-btn"
                  disabled={isSubmitting}
                  onClick={() => openAuthDialog("reopen")}
                >
                  Reopen Matter
                </Button>
              ) : (
                <Button
                  type="button"
                  className="common-btn"
                  disabled={!canClose || isSubmitting}
                  onClick={handleCloseClick}
                >
                  Close Matter
                </Button>
              )}
            </div>
            {!isClosed && closeCheck && !closeCheck.allowed && (
              <div className="text-danger fs-sm mt-2 text-end">
                {closeCheck.reasons.length ? (
                  closeCheck.reasons.map((reason) => (
                    <div key={reason}>{reason}</div>
                  ))
                ) : (
                  <div>This matter cannot be closed yet.</div>
                )}
              </div>
            )}
          </Tile>
          {children}
        </div>

        <div className="col-12 col-xl-6">
          <Tile
            heading="Matter Balances"
            className="px-4 pt-2 pb-3 overflow-auto h-100"
          >
            {COMPLETION_BALANCE_ROWS.map((row) => {
              const value = balances?.[row.key];
              const isOutstanding = nonZeroBalances.some(
                (item) => item.key === row.key,
              );
              return (
                <div
                  key={row.key}
                  className={`d-flex align-items-center ${
                    row.spaced ? "mt-4" : ""
                  }`}
                >
                  <div
                    className="d-flex justify-content-between border-bottom py-2"
                    style={{ width: "75%" }}
                  >
                    <span className="label">{row.label}:</span>
                    <span
                      className={`fw-semibold ${
                        isOutstanding ? "text-danger" : ""
                      }`}
                    >
                      {balancesLoading ? "..." : AmountFormat(value ?? 0)}
                    </span>
                  </div>
                  {row.key === "wipBalance" && (
                    <Button
                      type="button"
                      className="common-btn button-small ms-auto"
                      title={
                        canWriteOffWip
                          ? "Write Off WIP"
                          : "Requires the 'Write Off WIP' access right"
                      }
                      disabled={
                        !canWriteOffWip ||
                        isClosed ||
                        balancesLoading ||
                        !wipBalance ||
                        isSubmitting
                      }
                      onClick={() => openAuthDialog("writeOff")}
                    >
                      W/O
                    </Button>
                  )}
                </div>
              );
            })}
          </Tile>
        </div>
      </div>

      {confirm === "close" && (
        <ConfirmDialog
          content="Are you sure you want to close this matter?"
          isLoading={isSubmitting}
          onYes={handleCloseMatter}
          onNo={() => setConfirm(null)}
        />
      )}

      {authAction === "reopen" && (
        <AuthCodeDialog
          title="Reopen Matter"
          message={`Enter your authorisation code to reopen ${
            matter?.matterRef || "this matter"
          }.`}
          confirmText="Reopen"
          isLoading={isSubmitting}
          errorMessage={authError}
          onConfirm={handleReopenMatter}
          onClose={closeAuthDialog}
        />
      )}

      {authAction === "writeOff" && (
        <AuthCodeDialog
          title="Write Off WIP"
          message={`Enter your authorisation code to write off ${AmountFormat(
            wipBalance,
          )} of WIP.`}
          confirmText="Write Off"
          isLoading={isSubmitting}
          errorMessage={authError}
          onConfirm={handleWriteOffWip}
          onClose={closeAuthDialog}
        />
      )}
    </TileWrapper>
  );
};

export default MatterCompletionTab;
